import { useEffect } from 'react';
import useJob from '@/src/utils/hooks/superstack/useJob';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { SelectedDetailTab } from '@/src/store/superstack/jobs/JobSelectedStates';
import classNames from 'classnames/bind';
import { defaultLogoImgSrc } from '@/src/utils/api';
import LinkTag from '@/src/component/LinkTag';
import { JobLinks } from '@/data/superstack/JobLinks';
import SSImage from '@/src/component/superstack/base/SSImage';
import { LikeButton } from '@/src/component/superstack/base/button/Like';
import { Tabs } from '@/src/component/superstack/base/Tabs';
import {
  PositionBox,
  PositionSummaryBoxBody,
  ServiceBox,
  TeamBox,
} from '@/src/component/superstack/jobs/Position';
import { CardCompany } from '@/src/component/superstack/base/Card';
import useJobLike from '@/src/utils/hooks/superstack/useJobLike';
import { toggleLike } from '@/src/utils/ss_utils';
import { getDateFormatYMDDot } from '@/src/utils/datetimes';
import { ApplyLink } from '@/src/component/superstack/apply/ApplyButton';
import { CultureReviewResultBox } from '@/src/component/superstack/cultures/CultureReviewResultBox';
import { BoxStackCultureSummary } from '@/src/component/superstack/base/Box';
import useCultureReviewResultExist from '@/src/utils/hooks/superstack/useCultureReviewResultExist';

import styles, { colorBlue50, colorGray50 } from '@/styles/superstack/base/Card.module.scss';
import componentClasses from '@/styles/superstack/jobs/Component.module.scss';
import IconArrowRight from '@/asset/superstack/svgs/icon-arrow-right.svg';
import ArrowUpRight from '@/asset/superstack/svgs/arrow-up-right.svg';
import { CompanyJobIndex } from '@/src/component/superstack/companies/CompanyIndexes';
import { NeedPreferCultureBanner } from '@/src/component/superstack/cultures/CultureBanners';
import useCulturePreferResult from '@/src/utils/hooks/superstack/useCulturePreferResult';
import useCultureReviewExist from '@/src/utils/hooks/superstack/useCultureReviewExist';
import { useRouter } from 'next/router';
import IconBubbleTabsCulture from '@/asset/superstack/svgs/icon-bubble-tabs-culture.svg';
import { hasCultureBubbleClickedCookie } from '@/src/services/superstack/jobs/cookies';

const CardJobDetailInListHead = ({ selectedJob, isSaved, ...props }) => {
  const { data: { data: jobLike = {} } = {}, mutate: mutateLike } = useJobLike(selectedJob.id);
  const cx = classNames.bind(styles);
  const setSelectedDetailTab = useSetRecoilState(SelectedDetailTab);
  const router = useRouter();
  return (
    <section className={cx(['card-job-detail-in-list', 'in-list'])}>
      <>
        <section className={cx(['card-job-detail-head'])}>
          <div className={cx('head-info-wrapper')}>
            <div className={cx(['detail-link-wrapper'])}>
              <LinkTag
                isNextPage={true}
                href={JobLinks(selectedJob.id).find((link) => link.name === 'job_detail').url}
                target={'_blank'}
              >
                <span className={cx(['detail-link'])}>공고 상세 보기</span>
              </LinkTag>
              <ArrowUpRight width={14} height={14} fill={colorBlue50} />
            </div>
            {isSaved && selectedJob.date_liked && (
              <div className={cx('date-liked')}>
                {getDateFormatYMDDot(selectedJob.date_liked)}에 저장
              </div>
            )}
          </div>
          <div className={cx('left-right-wrapper')}>
            <div className={cx(['left'])}>
              <div className={cx(['header'])}>
                <div className={cx(['title-wrapper'])}>
                  <LinkTag
                    isNextPage={true}
                    href={JobLinks(selectedJob.id).find((link) => link.name === 'job_detail').url}
                  >
                    <div className={cx(['title'])}>
                      <div className={cx(['title-text'])}>{selectedJob.title}</div>
                      <IconArrowRight width={9} height={17} fill={colorGray50} />
                    </div>
                  </LinkTag>
                </div>
              </div>
              <div className={cx(['footer'])}>
                <CompanyJobIndex job={selectedJob} />
              </div>
            </div>
            <div className={cx('btn-wrapper')}>
              <ApplyLink
                job={selectedJob}
                style={{ width: '180px' }}
                bubbleStyle={{ position: 'absolute', top: '-37%', left: '15%' }}
              />
              <LikeButton
                like={jobLike.has_liked}
                likeListJobDetail={true}
                onClick={() => {
                  toggleLike(selectedJob.id, mutateLike);
                  {
                    props.deleteLike ? props.deleteLike(selectedJob.id) : '';
                  }
                }}
              />
            </div>
          </div>
          <BoxStackCultureSummary
            companyId={selectedJob?.company?.id}
            jobStacks={selectedJob.stack_details}
            handleClickStackAll={() => {
              setSelectedDetailTab('포지션 소개');
              const pathname = router.asPath.split('#')[0];
              const url = pathname + '#RequiredStackBox-anchor';
              router.replace(url, url);
            }}
            handleClickCultureAll={() => {
              setSelectedDetailTab('컬처코드');
            }}
            style={{ marginTop: '32px' }}
          />
        </section>
        {props.children}
      </>
    </section>
  );
};

const CardJobDetailInListBox = ({ jobId, userInfoData }) => {
  const { data: { data: job = {} } = {}, isLoading } = useJob(jobId);
  const { data: { data: { data: hasCultureReviewResult } = {} } = {} } =
    useCultureReviewResultExist(job?.company?.id);
  const { data: { data: preferResult = {} } = {}, isLoading: isLoadingPreferResult } =
    useCulturePreferResult(userInfoData.username);
  const { data: { data: { data: hasReview = false } = {} } = {} } = useCultureReviewExist(
    userInfoData.username,
  );

  const tabNames = [
    '포지션 소개',
    job?.service ? '프로덕트 소개' : null,
    hasCultureReviewResult ? '컬처코드' : null,
    job?.team ? '팀 소개' : null,
    '기업 정보',
  ];
  const [selectedDetailTab, setSelectedDetailTab] = useRecoilState(SelectedDetailTab);
  const cx = classNames.bind(styles);
  const componentCx = classNames.bind(componentClasses);

  useEffect(() => {
    if (!tabNames.includes(selectedDetailTab)) {
      setSelectedDetailTab(() => {
        return '포지션 소개';
      });
    }
  }, [tabNames, selectedDetailTab]);

  const needPreferCultureBanner = () => {
    return hasReview && !isLoadingPreferResult && !preferResult?.prefer_culture?.date_submitted;
  };
  return (
    <>
      <div style={{ position: 'relative' }}>
        <Tabs
          tabNames={tabNames}
          activeTabName={selectedDetailTab}
          updateContainer={setSelectedDetailTab}
          style={{ padding: '0 32px' }}
        />
        {!hasCultureBubbleClickedCookie() && tabNames.includes('컬처코드') && (
          <span style={{ position: 'absolute', left: '235px', top: '25px', zIndex: 1 }}>
            <IconBubbleTabsCulture />
          </span>
        )}
      </div>
      {!isLoading && (
        <section className={cx('detail-box')}>
          {selectedDetailTab === '포지션 소개' && (
            <section className={cx('info-section')}>
              <p className={cx('info-section-sub')} style={{ marginTop: '0' }}>
                포지션 정보
              </p>
              <PositionSummaryBoxBody
                job={job}
                style={{ marginTop: '24px', marginBottom: '40px' }}
              />
              <div style={{ marginTop: '28px' }}>
                <PositionBox
                  job={job}
                  useShowMore={false}
                  userInfoData={userInfoData}
                  withTitle={false}
                  onList={true}
                />
              </div>
            </section>
          )}
          {selectedDetailTab === '프로덕트 소개' && (
            <section className={cx('info-section')} style={{ padding: '4px 0 32px' }}>
              <div className={cx('info-section-sub')}>{job.service?.name}</div>
              <ServiceBox service={job.service} />
            </section>
          )}
          {selectedDetailTab === '컬처코드' && (
            <section className={cx('info-section')} style={{ padding: '4px 0 32px' }}>
              <div className={cx('info-section-sub')}>
                {needPreferCultureBanner() && <NeedPreferCultureBanner onList={true} />}
                <CultureReviewResultBox
                  companyId={job.company.id}
                  needComment={true}
                  needTitle={false}
                />
              </div>
            </section>
          )}
          {selectedDetailTab === '팀 소개' && (
            <section className={cx('info-section')} style={{ padding: '32px 0' }}>
              <TeamBox
                team={job.team}
                organization={JSON.parse(job.team?.organization_summary_kor || '[]')}
              />
            </section>
          )}
          {selectedDetailTab === '기업 정보' && (
            <section className={cx('info-section')} style={{ padding: '32px 0' }}>
              <div className={'flex-box'}>
                <SSImage
                  alt={'회사 로고'}
                  src={job.company?.logo || defaultLogoImgSrc}
                  width={36}
                  height={36}
                  objectFit={'contain'}
                  className={componentCx('company-logo')}
                  priority={true}
                />
                <p className={'body-text-1 ss-gray-100'} style={{ marginLeft: '14px' }}>
                  {job.company?.name}
                </p>
              </div>
              <div style={{ marginTop: '24px' }}>
                <CardCompany company={job.company} />
              </div>
            </section>
          )}
        </section>
      )}
    </>
  );
};

export { CardJobDetailInListHead, CardJobDetailInListBox };
